// Submodules
import { enterArMode, leaveArMode, resumeArMode, pauseArMode, showArFeaturePoints } from '../app-api/AppAction';
import { events } from '../app-api/WebAction';

import core = egr.wcf.core;
import pl = egr.wcf.pl;
import utils = egr.wcf.utils;

export type ArScreenshot = {
    data: Blob;
    width: number;
    height: number;
    output: 'blob';
} | {
    data: string;
    width: number;
    height: number;
    output: 'dataurl';
}

interface ArPlane {
    extent: Array<number>;
    modelMatrix: Array<number>;
    identifier: number;
}

export interface ArState {
    started: boolean;
    cameraIsFrozen: boolean;
    objectIsPlaced: boolean;
}

export abstract class ArCore {
    public readonly eventArScreenshot: utils.ListenableEvent<ArScreenshot, ArCore> = new utils.ListenableEvent<ArScreenshot, ArCore>(this);
    public readonly stateEvent: utils.ListenableEvent<ArState, ArCore> = new utils.ListenableEvent<ArState, ArCore>(this);
    public screenshotType: 'blob' | 'dataurl' = 'blob';

    protected defaultTool: pl.tool.TouchDefault;
    protected defaultToolBackup: core.tool.Tool | null;
    protected planeYOffset: number;

    private mShadowPlane: core.rendering.ShadowPlane | null;
    private mColorCurves: BABYLON.ColorCurves | null;

    private state: ArState = {
        objectIsPlaced: false,
        started: false,
        cameraIsFrozen: false
    };

    private mScreenshotPromise: Promise<void> | null;

    private mFovBackup: number;
    private mCameraModeBackup: core.view.CameraMode;
    private mDirectionBackup: BABYLON.Vector3;
    private mExposureBackup: number;
    private mShadowTransparencyBackup: number;

    protected get activeView(): core.view.View {
        if (this.coreApp.viewer.activeView == null) {
            throw new Error('no active view');
        }
        return this.coreApp.viewer.activeView;
    }

    constructor(protected coreApp: core.Application) {
        this.mScreenshotPromise = null;
        this.defaultTool = new pl.tool.TouchDefault(coreApp);
        this.defaultTool.moveYBelowGroundEnabled = false;

        events.arUpdate.addListener(this.onHandleUpdate);
        events.arPlanesAdded.addListener(this.onHandlePlanesAddedOrUpdated);
        events.arPlanesUpdated.addListener(this.onHandlePlanesAddedOrUpdated);
        events.arPlanesRemoved.addListener(this.onHandlePlanesRemoved);
        window.addEventListener('orientationchange', this.thawOnOrientationChange);

        // get shadowplane
        this.mShadowPlane = null;
        for (const tGen of coreApp.rendering.shadowGenerators) {
            if (tGen instanceof core.rendering.ShadowPlane) {
                this.mShadowPlane = tGen;
            }
        }

        // get color curves
        this.mColorCurves = coreApp.model.materials.pbrImageProcessing.colorCurves;
    }

    public getState = (): ArState => {
        return this.state;
    };

    public async enterAr(): Promise<void> {
        if (this.state.started) {
            return;
        }

        this.updateState({objectIsPlaced: false});

        this.coreApp.model.scene.clearColor = new BABYLON.Color4(0, 0, 0, 0); // make WebGL Canvas transparent

        this.startTool();

        this.mFovBackup = this.activeView.camera.fov;
        this.mCameraModeBackup = this.activeView.cameraControl.cameraMode;
        this.mDirectionBackup = this.activeView.cameraControl.direction.clone();
        if (this.mColorCurves != null) {
            this.mExposureBackup = this.mColorCurves.globalExposure;
        }

        this.activeView.cameraControl.controlsEnabled = false;
        this.activeView.cameraControl.cameraMode = core.view.CameraMode.Perspective;
        this.planeYOffset = 0;

        this.showCanvas(false);
        if (this.mShadowPlane != null) {
            this.mShadowTransparencyBackup = this.mShadowPlane.transparency;
            this.mShadowPlane.setTransparency(0.4);
        }

        await this.onEnterAr();

        this.coreApp.viewer.requestRenderFrame();

        await showArFeaturePoints(true);

        this.updateState({started: true, cameraIsFrozen: false});

        return enterArMode();
    }

    public async leaveAr(): Promise<void> {
        if (!this.state.started) {
            return;
        }

        this.placeModel(utils.math.VECTOR3_ZERO, true);
        this.showCanvas(true);

        this.coreApp.model.scene.clearColor = new BABYLON.Color4(1, 1, 1, 1);

        // rest tool
        this.restoreTool();

        // reset exposure
        if (this.mColorCurves != null) {
            this.mColorCurves.globalExposure = this.mExposureBackup;
        }

        // reset camera
        this.activeView.cameraControl.controlsEnabled = true;
        this.activeView.cameraControl.beginChange();
        this.activeView.cameraControl.setDirection(this.mDirectionBackup);
        this.activeView.cameraControl.setFov(this.mFovBackup);
        this.activeView.cameraControl.cameraMode = this.mCameraModeBackup;
        this.activeView.cameraControl.zoomToFitElements(null, this.activeView.cameraControl.dblClickZoomToFitOptions);
        this.activeView.cameraControl.endChange();

        if (this.mShadowPlane != null) {
            this.mShadowPlane.setTransparency(this.mShadowTransparencyBackup);
        }

        await this.onLeaveAr();

        this.coreApp.viewer.requestRenderFrame();

        this.updateState({started: false});

        return leaveArMode();
    }

    public onToggleCameraFreeze = async (): Promise<void> => {
        if (this.state.cameraIsFrozen) {
            await resumeArMode();
        } else {
            await pauseArMode();
        }
        this.updateState({cameraIsFrozen: this.state.cameraIsFrozen === false});
    };

    protected abstract onEnterAr(): Promise<void>;
    protected abstract onLeaveAr(): Promise<void>;

    protected updateState(newState: Partial<ArState>): void {
        this.state = {...this.state, ...newState};
        this.stateEvent.trigger(this.state);
    }

    protected startTool(): void {
        this.defaultToolBackup = this.coreApp.tools.defaultTool;
        this.coreApp.tools.startDefaultTool();
    }

    protected restoreTool(): void {
        this.coreApp.tools.defaultTool = this.defaultToolBackup;
        this.coreApp.tools.startDefaultTool();
    }

    protected placeModel(pPos: BABYLON.Vector3, onStart: boolean): void {
        const tBounds: core.mdl.BoundingBox = core.mdl.BoundingBox.FromElements(this.coreApp.model.elements);
        const tCenter: BABYLON.Vector3 = tBounds.getCenter();
        const tPosDif: BABYLON.Vector3 =
            new BABYLON.Vector3(
                pPos.x - tCenter.x,
                0,
                pPos.z - tCenter.z);
        if (tBounds.isValid()) {
            for (const tElement of this.coreApp.model.elements) {
                tElement.node.position.addInPlace(tPosDif);
                tElement.updateTransformation();
            }
        }

        this.planeYOffset = -pPos.y;
    }

    protected thawOnOrientationChange = (): void => {
        if (this.state.cameraIsFrozen) {
            this.onToggleCameraFreeze();
        }
    };

    private onHandleUpdate = async (pParam?: string): Promise<void> => {
        if (pParam == null || !this.state.started || this.mScreenshotPromise != null) {
            return;
        }
        const tCamData: {
            position: Array<number>,
            orientation: Array<number>,
            projectionMatrix: Array<number>,
            ambientIntensity: number,
            cameraFrame?: string
        } = JSON.parse(pParam);

        if (!this.state.cameraIsFrozen) {
            // adjust exposure
            if (this.mColorCurves != null) {
                const tIntensity: number = utils.math.clamp(tCamData.ambientIntensity, 0, 1);
                this.mColorCurves.globalExposure = (tIntensity * 0.9 - 1.0) * 100; // [-100, -10]
            }

            // get fov
            const tFov: number = Math.atan(1.0 / tCamData.projectionMatrix[5]) * 2.0;
            this.activeView.cameraControl.setFov(tFov);

            // get position
            const tPos: BABYLON.Vector3 = BABYLON.Vector3.FromArray(tCamData.position);
            tPos.z = -tPos.z;
            tPos.y += this.planeYOffset;

            // get direction and up vector
            const tQuat: BABYLON.Quaternion = BABYLON.Quaternion.FromArray(tCamData.orientation);
            tQuat.z = -tQuat.z;
            tQuat.w = -tQuat.w;

            const tDir: BABYLON.Vector3 = utils.math.rotateVector3(utils.math.VECTOR3_Z, tQuat);
            const tUp: BABYLON.Vector3 = utils.math.rotateVector3(utils.math.VECTOR3_Y, tQuat);

            this.activeView.cameraControl.beginChange();
            this.activeView.cameraControl.setDirection(tDir, tUp);
            this.activeView.cameraControl.setPosition(tPos);
            this.activeView.cameraControl.endChange();
        }

        // create screenshot
        if (utils.string.isNotNullOrEmpty(tCamData.cameraFrame)) {
            this.mScreenshotPromise = this.createScreenshot(tCamData.cameraFrame);
            await this.mScreenshotPromise;
            this.mScreenshotPromise = null;
        }
    };

    private onHandlePlanesAddedOrUpdated = async (pParam?: string): Promise<void> => {
        if (pParam == null || !this.state.started) {
            return;
        }
        const tPlanes: Array<ArPlane> = JSON.parse(pParam);
        for (const tPlane of tPlanes) {
            this.addOrUpdatePlane(tPlane);
        }
    };

    private onHandlePlanesRemoved = async (pParam?: string): Promise<void> => {
        if (pParam == null || !this.state.started) {
            return;
        }
    };

    private addOrUpdatePlane(pPlane: ArPlane): void {
        if (this.state.objectIsPlaced) {
            return;
        }

        const tMatrix: BABYLON.Matrix = BABYLON.Matrix.FromArray(pPlane.modelMatrix);
        const tScale: BABYLON.Vector3 = BABYLON.Vector3.Zero();
        const tPos: BABYLON.Vector3 = BABYLON.Vector3.Zero();
        const tRot: BABYLON.Quaternion = BABYLON.Quaternion.Identity();
        if (tMatrix.decompose(tScale, tRot, tPos)) {
            // convert from right to left handed
            tPos.z = -tPos.z;

            // found first plane -> place model
            this.updateState({ objectIsPlaced: true });
            this.placeModel(tPos, false);
            void showArFeaturePoints(false);
            this.showCanvas(true);
        }
    }

    private showCanvas(pValue: boolean): void {
        if (pValue) {
            this.activeView.overlayDiv.style.visibility = '';
            this.activeView.viewer.canvas.style.visibility = '';
        } else {
            this.activeView.overlayDiv.style.visibility = 'hidden';
            this.activeView.viewer.canvas.style.visibility = 'hidden';
        }
    }

    private createScreenshot(pCameraFrameUri: string): Promise<void> {
        const tPromise: Promise<void> = new Promise<void>((resolve) => {
            const tImg: HTMLImageElement = document.createElement('img');
            const tWidth: number = this.activeView.renderWidth;
            const tHeight: number = this.activeView.renderHeight;

            tImg.onload = async () => {
                if (this.screenshotType === 'blob') {
                    const data: Blob = await this.coreApp.viewer.createScreenshot(
                        this.activeView,
                        {
                            width: tWidth,
                            height: tHeight,
                            mimeType: 'image/jpeg',
                            onDrawBackground: (pContext) => {
                                pContext.drawImage(tImg, 0, 0, tWidth, tHeight);
                            }
                        },
                        true
                    );
                    resolve();
                    this.eventArScreenshot.trigger({ data, width: tWidth, height: tHeight, output: 'blob' });
                } else {
                    const data: string = await this.coreApp.viewer.createScreenshot(
                        this.activeView,
                        {
                            width: tWidth,
                            height: tHeight,
                            mimeType: 'image/jpeg',
                            onDrawBackground: (pContext) => {
                                pContext.drawImage(tImg, 0, 0, tWidth, tHeight);
                            }
                        },
                        false
                    );
                    resolve();
                    this.eventArScreenshot.trigger({ data, width: tWidth, height: tHeight, output: 'dataurl' });
                }
            };
            tImg.src = pCameraFrameUri;
        });
        return (tPromise);
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/submodules/ar-manager/ArCore.ts